import { useParams, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { learnerApi } from '@/api/b2b'
import { useAuthStore } from '@/store/authStore'
import { useAiqSession } from '@/hooks/useAiq'
import { AiqQuizStage, type AiqCompletionResult } from '@/components/learner/AiqQuizStage'
import { Play, Loader2, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react'

type Phase = 'intro' | 'quiz' | 'result'

interface QuizResult {
  totalQuestions: number
}

export default function PreAiqTest() {
  const { t } = useTranslation('learner')
  const { classProductId } = useParams<{ classProductId: string }>()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const userName = user?.fullName || t('aiqTest.defaultName')
  const [phase, setPhase] = useState<Phase>('intro')
  const [result, setResult] = useState<QuizResult | null>(null)

  // Get course info to retrieve productId (same format as MyCourses.tsx for cache sharing)
  const { data: coursesData, isLoading: isLoadingCourse } = useQuery({
    queryKey: ['myClassCourses'],
    queryFn: () => learnerApi.getMyClassCourses(),
    staleTime: 5 * 60 * 1000, // 5분간 캐시 유지 (페이지 이동 시 재요청 방지)
    gcTime: 10 * 60 * 1000, // 10분간 가비지 컬렉션 방지
  })

  const courses = coursesData?.data?.data || []
  const currentCourse = courses.find(
    (c) => c.classProductId === Number(classProductId)
  )
  const productId = currentCourse?.productId
  const firstCourseName = currentCourse?.isBundle
    ? (currentCourse?.bundleContents?.[0]?.courses?.[0]?.bareTitle || currentCourse?.bundleContents?.[0]?.courses?.[0]?.title || currentCourse?.productName || t('aiqTest.firstCourseFallback'))
    : (currentCourse?.courses?.[0]?.bareTitle || currentCourse?.courses?.[0]?.title || currentCourse?.productName || t('aiqTest.firstCourseFallback'))

  // AIQ Session hook
  const aiqSession = useAiqSession(
    'pre',
    productId,
    classProductId ? Number(classProductId) : undefined
  )

  const handleStart = async () => {
    try {
      await aiqSession.startSession.mutateAsync()
      setPhase('quiz')
    } catch (error) {
      console.error('Failed to start Pre-AIQ session:', error)
    }
  }

  const handleQuizComplete = (completionResult: AiqCompletionResult) => {
    setResult({
      totalQuestions: completionResult.totalQuestions,
    })
    setPhase('result')
  }

  const handleBack = () => {
    navigate('/my/aiq')
  }


  // Loading state
  if (isLoadingCourse) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 w-8 h-8 animate-spin text-primary-600" />
          <p className="text-gray-600">{t('aiqTest.loading')}</p>
        </div>
      </div>
    )
  }

  // Course not found
  if (!currentCourse) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 w-16 h-16 text-red-400" />
          <p className="text-gray-600">{t('aiqTest.courseNotFound')}</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 mt-4 text-white rounded-lg bg-primary-600 hover:bg-primary-700"
          >
            {t('aiqTest.goBack')}
          </button>
        </div>
      </div>
    )
  }

  // Intro Phase
  if (phase === 'intro') {
    return (
      <div className="flex justify-center items-center p-4 min-h-screen bg-gray-50 sm:p-8">
        <div className="p-6 w-full max-w-2xl bg-white rounded-xl shadow-lg sm:p-8">
          {/* AIQ BI */}
          <div className="mb-8 text-center">
            <div className="flex gap-3 justify-center items-center mb-2">
              <img src="/aiq-logo.png" alt="AIQ" className="h-12" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
              {t('aiqTest.pre.title')}
            </h1>
          </div>

          {/* 안내 멘트 */}
          <div className="mb-8 space-y-4">
            <p className="text-lg text-gray-700">
              {t('aiqTest.pre.greeting', { userName })}
            </p>
            <p className="text-base leading-relaxed text-gray-600">
              {t('aiqTest.pre.description', { userName })}
            </p>
            <p className="text-base leading-relaxed text-gray-600">
              {t('aiqTest.testQuestions')}
            </p>
            <p className="text-sm leading-relaxed text-gray-500">
              {t('aiqTest.resultNote')}
            </p>
          </div>

          {/* 문제 수 정보 */}
          <div className="flex gap-3 items-center p-4 mb-8 bg-gray-50 rounded-lg border">
            <Play className="flex-shrink-0 w-5 h-5 text-primary-600" />
            <div>
              <div className="text-sm text-gray-600">{t('aiqTest.questionCount')}</div>
              <div className="text-lg font-semibold text-gray-900">{t('aiqTest.questionCountValue')}</div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleBack}
              className="flex flex-1 gap-2 justify-center items-center px-4 py-3 text-gray-700 rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('aiqTest.goBack')}
            </button>
            <button
              onClick={handleStart}
              disabled={aiqSession.isStarting}
              className="flex flex-1 gap-2 justify-center items-center px-4 py-3 text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {aiqSession.isStarting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {t('aiqTest.startTest')}
            </button>
          </div>

          {aiqSession.startError && (
            <p className="mt-4 text-sm text-center text-red-600">
              {t('aiqTest.startFailed')}
            </p>
          )}
        </div>
      </div>
    )
  }

  // Quiz Phase - Use AiqQuizStage component
  if (phase === 'quiz' && aiqSession.sessionId && aiqSession.quizzes.length > 0) {
    return (
      <div className="p-8 min-h-screen bg-gray-50">
        <AiqQuizStage
          productId={productId || 0}
          testType="pre"
          sessionId={aiqSession.sessionId}
          quizzes={aiqSession.quizzes}
          resumeFromIndex={aiqSession.resumeFromIndex}
          previousResponses={aiqSession.previousResponses}
          onComplete={handleQuizComplete}
          onBack={handleBack}
          submitResponse={aiqSession.submitResponse}
          completeSession={aiqSession.completeSessionFn}
        />
      </div>
    )
  }

  // Quiz loading state
  if (phase === 'quiz' && (!aiqSession.sessionId || aiqSession.quizzes.length === 0)) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 w-8 h-8 animate-spin text-primary-600" />
          <p className="text-gray-600">{t('aiqTest.loadingQuiz')}</p>
        </div>
      </div>
    )
  }

  // Result Phase
  if (phase === 'result' && result) {
    return (
      <div className="flex justify-center items-center p-4 min-h-screen bg-gray-50 sm:p-8">
        <div className="p-6 w-full max-w-2xl bg-white rounded-xl shadow-lg sm:p-8">
          {/* AIQ BI */}
          <div className="mb-8 text-center">
            <div className="flex gap-3 justify-center items-center mb-2">
              <img src="/aiq-logo.png" alt="AIQ" className="h-12" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
              {t('aiqTest.pre.completedTitle')}
            </h1>
          </div>

          {/* 안내 멘트 */}
          <div className="mb-8 space-y-4">
            <p className="text-lg text-gray-700">
              {t('aiqTest.pre.completedGreeting', { userName })}
            </p>
            <p className="text-base leading-relaxed text-gray-600">
              {t('aiqTest.pre.completedCourseStart', { courseName: firstCourseName })}
            </p>
            <p className="text-base leading-relaxed text-gray-600">
              {t('aiqTest.pre.completedEncouragement')}
            </p>
            <p className="text-sm leading-relaxed text-gray-500">
              {t('aiqTest.resultNotePost')}
            </p>
          </div>

          {/* 결과 정보 */}
          <div className="flex gap-3 items-center p-4 mb-8 bg-gray-50 rounded-lg border">
            <CheckCircle className="flex-shrink-0 w-5 h-5 text-green-500" />
            <div>
              <div className="text-sm text-gray-600">{t('aiqTest.questionCount')}</div>
              <div className="text-lg font-semibold text-gray-900">{t('aiqTest.completedCount', { count: result.totalQuestions })}</div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleBack}
              className="flex-1 px-4 py-3 text-gray-700 rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
            >
              {t('aiqTest.goToAiq')}
            </button>
            <button
              onClick={() => {
                const contentId = currentCourse?.contentId || currentCourse?.bundleContents?.[0]?.contentId
                navigate(contentId ? `/learn/${classProductId}/${contentId}` : `/learn/${classProductId}`)
              }}
              className="flex-1 px-4 py-3 text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
            >
              {t('aiqTest.startLearning')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}
